import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleCheck, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LogoComponent } from '../../components/logo/logo.component';
import { AuthService } from '../../services/auth.service';
import { CompanyService } from '../../services/company.service';

type PageState = 'confirming' | 'done' | 'error';

/**
 * Where the "Neue IBAN bestätigen" e-mail link lands (/confirm-iban?token=…).
 * Works without login: the single-use token proves access to the mailbox.
 */
@Component({
  selector: 'app-confirm-iban',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    LogoComponent,
  ],
  providers: [provideIcons({ lucideCircleCheck, lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './confirm-iban.component.html',
})
export class ConfirmIbanComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly authService = inject(AuthService);

  readonly state = signal<PageState>('confirming');
  readonly iban = signal<string | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly loggedIn = this.authService.isLoggedIn();

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    if (!token) {
      this.fail('Ungültiger Bestätigungslink.');
      return;
    }
    this.companyService.confirmBankAccountChange(token).subscribe({
      next: (account) => {
        this.iban.set(account.iban_masked);
        this.state.set('done');
        if (this.loggedIn) this.companyService.reloadCompanyData();
      },
      error: (err) => this.fail(err.error?.detail || 'Die IBAN konnte nicht bestätigt werden.'),
    });
  }

  private fail(message: string): void {
    this.errorMessage.set(message);
    this.state.set('error');
  }
}
